import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "driver", label: "Driver" },
  { value: "wood", label: "Wood" },
  { value: "jern", label: "Jern" },
  { value: "putter", label: "Putter" },
  { value: "bag", label: "Bag" },
  { value: "wedge", label: "Wedge" },
  { value: "hybrid", label: "Hybrid" },
  { value: "tilbehør", label: "Tilbehør" },
  { value: "annet", label: "Annet" },
];

const conditions = [
  { value: "ny", label: "Ny" },
  { value: "utmerket", label: "Utmerket" },
  { value: "god", label: "God" },
  { value: "greit", label: "Greit" },
];

const flexOptions = [
  { value: "ladies", label: "Ladies (L)" },
  { value: "senior", label: "Senior (A)" },
  { value: "regular", label: "Regular (R)" },
  { value: "stiff", label: "Stiff (S)" },
  { value: "x-stiff", label: "Extra Stiff (X)" },
];

const lengthOptions = [
  { value: "oversized", label: "Oversized (+)" },
  { value: "standard", label: "Standard" },
  { value: "undersized", label: "Undersized (-)" },
];

const handOptions = [
  { value: "right", label: "Høyre" },
  { value: "left", label: "Venstre" },
];

const shaftMaterialOptions = [
  { value: "graphite", label: "Graphite" },
  { value: "steel", label: "Steel" },
];

const lieOptions = [
  { value: "-2", label: "2° Flat" },
  { value: "-1.5", label: "1.5° Flat" },
  { value: "-1", label: "1° Flat" },
  { value: "-0.5", label: "0.5° Flat" },
  { value: "0", label: "0° (Standard)" },
  { value: "0.5", label: "0.5° Upright" },
  { value: "1", label: "1° Upright" },
  { value: "1.5", label: "1.5° Upright" },
  { value: "2", label: "2° Upright" },
];

const brandOptions = [
  { value: "callaway", label: "Callaway" },
  { value: "taylormade", label: "TaylorMade" },
  { value: "titleist", label: "Titleist" },
  { value: "ping", label: "Ping" },
  { value: "cobra", label: "Cobra" },
  { value: "mizuno", label: "Mizuno" },
  { value: "cleveland", label: "Cleveland" },
  { value: "srixon", label: "Srixon" },
  { value: "wilson", label: "Wilson" },
  { value: "odyssey", label: "Odyssey" },
  { value: "scotty cameron", label: "Scotty Cameron" },
  { value: "annet", label: "Annet" },
];

// Norwegian cities with coordinates
const norwegianCities = [
  { name: "Oslo", postal_code: "0150", lat: 59.9139, lon: 10.7522 },
  { name: "Bergen", postal_code: "5003", lat: 60.3913, lon: 5.3221 },
  { name: "Trondheim", postal_code: "7030", lat: 63.4305, lon: 10.3951 },
  { name: "Stavanger", postal_code: "4001", lat: 58.9700, lon: 5.7331 },
  { name: "Kristiansand", postal_code: "4600", lat: 58.1599, lon: 7.9959 },
  { name: "Tromsø", postal_code: "9008", lat: 69.6492, lon: 18.9553 },
  { name: "Drammen", postal_code: "3000", lat: 59.7439, lon: 10.2045 },
  { name: "Fredrikstad", postal_code: "1600", lat: 59.2181, lon: 10.9298 },
  { name: "Sandnes", postal_code: "4301", lat: 58.8524, lon: 5.7358 },
  { name: "Bodø", postal_code: "8001", lat: 67.2804, lon: 14.4049 },
];

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    condition: "",
    location: "",
    images: "",
    seller_name: "",
    seller_email: "",
    seller_phone: "",
    flex: "",
    length: "",
    hand: "",
    brand: "",
    shaft_material: "",
    lie: "",
    postal_code: "",
    latitude: null,
    longitude: null,
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error("Du må logge inn for å legge ut en annonse");
      navigate("/login", { state: { from: "/create" } });
    }
  }, [isAuthenticated, navigate]);

  // Pre-fill seller info from user data
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        seller_name: prev.seller_name || user.name || "",
        seller_email: prev.seller_email || user.email || "",
      }));
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (cityName) => {
    const city = norwegianCities.find(c => c.name === cityName);
    if (city) {
      setFormData((prev) => ({
        ...prev,
        location: cityName,
        postal_code: city.postal_code,
        latitude: city.lat,
        longitude: city.lon,
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        if (data.secure_url) {
          uploadedUrls.push(data.secure_url);
        }
      }

      setUploadedImages([...uploadedImages, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} bilde(r) lastet opp!`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Kunne ikke laste opp bilder");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setUploadedImages(uploadedImages.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.description || !formData.price || !formData.category || 
        !formData.condition || !formData.location || !formData.seller_name || 
        !formData.seller_email || !formData.seller_phone) {
      toast.error("Vennligst fyll ut alle felt");
      return;
    }

    try {
      setLoading(true);
      
      // Combine uploaded images with any manually entered URLs
      const manualUrls = formData.images
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
      
      const allImages = [...uploadedImages, ...manualUrls];

      const listingData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        condition: formData.condition,
        location: formData.location,
        images: allImages,
        seller_name: formData.seller_name,
        seller_email: formData.seller_email,
        seller_phone: formData.seller_phone,
        // Only include optional fields if they have values
        ...(formData.flex && { flex: formData.flex }),
        ...(formData.length && { length: formData.length }),
        ...(formData.hand && { hand: formData.hand }),
        ...(formData.brand && { brand: formData.brand }),
        ...(formData.shaft_material && { shaft_material: formData.shaft_material }),
        ...(formData.lie && { lie: formData.lie }),
      };

      const response = await axios.post(`${API}/listings`, listingData, {
        headers: getAuthHeaders(),
      });
      toast.success("Annonse opprettet!");
      navigate(`/listing/${response.data.id}`);
    } catch (error) {
      console.error("Error creating listing:", error);
      if (error.response?.status === 401) {
        toast.error("Du må logge inn for å opprette en annonse");
        navigate("/login", { state: { from: "/create" } });
      } else {
        toast.error("Kunne ikke opprette annonse");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
      {/* Navigation */}
      <Navbar showBackButton={true} showWedgeGuide={false} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-gray-900">
              Legg ut ny annonse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Tittel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="F.eks. Callaway Driver 2023"
                  className="mt-1"
                  data-testid="title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Beskrivelse *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Beskriv utstyret ditt i detalj..."
                  rows={4}
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              {/* Price and Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Pris (kr) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    placeholder="2500"
                    className="mt-1"
                    data-testid="price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Lokasjon *</Label>
                  <Select value={formData.location} onValueChange={handleLocationChange} required>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Velg din by" />
                    </SelectTrigger>
                    <SelectContent>
                      {norwegianCities.map((city) => (
                        <SelectItem key={city.name} value={city.name}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Category and Condition */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Kategori *</Label>
                  <Select value={formData.category} onValueChange={(value) => handleChange("category", value)}>
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Velg kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tilstand *</Label>
                  <Select value={formData.condition} onValueChange={(value) => handleChange("condition", value)}>
                    <SelectTrigger className="mt-1" data-testid="condition-select">
                      <SelectValue placeholder="Velg tilstand" />
                    </SelectTrigger>
                    <SelectContent>
                      {conditions.map((cond) => (
                        <SelectItem key={cond.value} value={cond.value}>
                          {cond.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Advanced Specifications */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Spesifikasjoner (valgfritt)</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Merke</Label>
                    <Select value={formData.brand} onValueChange={(value) => handleChange("brand", value)}>
                      <SelectTrigger className="mt-1" data-testid="brand-select">
                        <SelectValue placeholder="Velg merke" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandOptions.map((brand) => (
                          <SelectItem key={brand.value} value={brand.value}>
                            {brand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hånd (høyre/venstre)</Label>
                    <Select value={formData.hand} onValueChange={(value) => handleChange("hand", value)}>
                      <SelectTrigger className="mt-1" data-testid="hand-select">
                        <SelectValue placeholder="Velg hånd" />
                      </SelectTrigger>
                      <SelectContent>
                        {handOptions.map((hand) => (
                          <SelectItem key={hand.value} value={hand.value}>
                            {hand.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label>Stivhet/Flex</Label>
                    <Select value={formData.flex} onValueChange={(value) => handleChange("flex", value)}>
                      <SelectTrigger className="mt-1" data-testid="flex-select">
                        <SelectValue placeholder="Velg flex" />
                      </SelectTrigger>
                      <SelectContent>
                        {flexOptions.map((flex) => (
                          <SelectItem key={flex.value} value={flex.value}>
                            {flex.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Lengde</Label>
                    <Select value={formData.length} onValueChange={(value) => handleChange("length", value)}>
                      <SelectTrigger className="mt-1" data-testid="length-select">
                        <SelectValue placeholder="Velg lengde" />
                      </SelectTrigger>
                      <SelectContent>
                        {lengthOptions.map((length) => (
                          <SelectItem key={length.value} value={length.value}>
                            {length.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Shaft-materiale</Label>
                    <Select value={formData.shaft_material} onValueChange={(value) => handleChange("shaft_material", value)}>
                      <SelectTrigger className="mt-1" data-testid="shaft-material-select">
                        <SelectValue placeholder="Velg materiale" />
                      </SelectTrigger>
                      <SelectContent>
                        {shaftMaterialOptions.map((material) => (
                          <SelectItem key={material.value} value={material.value}>
                            {material.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Lie vinkel (for jern)</Label>
                  <Select value={formData.lie} onValueChange={(value) => handleChange("lie", value)}>
                    <SelectTrigger className="mt-1" data-testid="lie-select">
                      <SelectValue placeholder="Velg lie vinkel" />
                    </SelectTrigger>
                    <SelectContent>
                      {lieOptions.map((lie) => (
                        <SelectItem key={lie.value} value={lie.value}>
                          {lie.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-500 mt-1">
                    Lie vinkelen påvirker hvordan køllen ligger mot bakken ved impact
                  </p>
                </div>
              </div>

              {/* Images */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Bilder</h3>
                
                {/* Image Guidelines */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Tips for gode bilder</h4>
                      <ul className="text-sm text-blue-800 space-y-1.5">
                        <li><strong>Antall:</strong> Last opp minst 3-5 bilder for best resultat</li>
                        <li><strong>Kølleansikt/riller:</strong> Ta et tydelig nærbildet som viser ansiktet og rillene på køllen</li>
                        <li><strong>Grep:</strong> Ta et tydelig bilde av grepet for å vise tilstand</li>
                        <li><strong>Helhet:</strong> Ta 2-3 bilder av hele køllen fra forskjellige vinkler</li>
                        <li><strong>Kvalitet:</strong> God belysning, skarpt fokus, og rent bakgrunn gir best inntrykk</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Image Upload */}
                <div className="mb-4">
                  <Label htmlFor="image-upload" className="block mb-2">
                    Last opp bilder fra enheten din
                  </Label>
                  <div className="flex items-center gap-3">
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Velg bilder
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImages}
                      data-testid="image-upload-input"
                    />
                    {uploadingImages && (
                      <span className="text-sm text-gray-600">Laster opp...</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Du kan laste opp flere bilder samtidig (JPG, PNG, etc.)
                  </p>
                </div>

                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mb-4">
                    <Label className="block mb-2">Opplastede bilder ({uploadedImages.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {uploadedImages.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Uploaded ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-image-${index}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual URL Input (Optional) */}
                <div>
                  <Label htmlFor="images">Eller legg til bilde-URLer manuelt (valgfritt)</Label>
                  <Input
                    id="images"
                    value={formData.images}
                    onChange={(e) => handleChange("images", e.target.value)}
                    placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                    className="mt-1"
                    data-testid="images-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Legg til bilde-URLer separert med komma
                  </p>
                </div>
              </div>

              {/* Seller Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Kontaktinformasjon</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="seller_name">Navn *</Label>
                    <Input
                      id="seller_name"
                      value={formData.seller_name}
                      onChange={(e) => handleChange("seller_name", e.target.value)}
                      placeholder="Ditt navn"
                      className="mt-1"
                      data-testid="seller-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seller_email">E-post *</Label>
                    <Input
                      id="seller_email"
                      type="email"
                      value={formData.seller_email}
                      onChange={(e) => handleChange("seller_email", e.target.value)}
                      placeholder="din@email.no"
                      className="mt-1"
                      data-testid="seller-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seller_phone">Telefon *</Label>
                    <Input
                      id="seller_phone"
                      value={formData.seller_phone}
                      onChange={(e) => handleChange("seller_phone", e.target.value)}
                      placeholder="+47 123 45 678"
                      className="mt-1"
                      data-testid="seller-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-lg"
                  disabled={loading}
                  data-testid="submit-listing-button"
                >
                  {loading ? "Oppretter..." : "Legg ut annonse"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateListingPage;